import type {
  CreateCallback,
  UpdateCallback,
  UpdateWithApprovalCallback,
} from '~publish/legacy/composer/composer/types'
import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'

import type { PostStatus, ScheduleOption } from '../graphqlEnums'
import type { QueueingTypes } from '~publish/legacy/composer/composer/AppConstants'
import type { ChannelVariant } from '../store/postSlice/types'

// The values type of the QueueingTypes constants object; used by the GraphQL
// save path to pass the correct queueing type to the completion handler.
export type QueueingType = (typeof QueueingTypes)[keyof typeof QueueingTypes]

/**
 * The direction of an approval change carried by a save.
 *
 * Consumed by the legacy save path (which sends it as `approval_mode` to the
 * REST endpoint) and by the GraphQL path (which maps it to `needsApproval` /
 * `approvalChange`). Both use it to pick the success-confirmation copy — see
 * `GetComposerConfirmationMessage`.
 */
export enum ApprovalMode {
  REQUEST_APPROVAL = 'REQUEST_APPROVAL',
  REVERT_APPROVAL = 'REVERT_APPROVAL',
}

/**
 * The post a completed save should link to, for the success toast's "View post"
 * action and the tag prefill on "Create another".
 *
 * `status` is the GraphQL `PostStatus`, which already folds pending-approval in
 * as `needs_approval` — the REST response splits the same information across
 * `status` + `needs_approval`, so the legacy path maps into this shape.
 */
export type SavedPostRef = {
  id: string
  channelId: string
  status: PostStatus
  /**
   * Absent when the post's tags are unknown, which is distinct from an empty
   * array: the toast's "Create another" prefills `{ tags: [] }` for the latter
   * and prefills nothing for the former.
   */
  tagIds?: string[]
}

// The save surface the composer footer depends on; both the legacy and GraphQL
// implementations return this shape so `useSaveCallbacks` can route between them.
export type SaveCallbacks = {
  schedulePost: CreateCallback
  saveAsDraft: CreateCallback
  saveAndRequestApproval: CreateCallback
  updatePost: UpdateCallback
  updatePostWithApproval: UpdateWithApprovalCallback
}

// The subset of a selected composer profile the transport needs: a draft
// targets a service and each matching profile becomes one post variant.
export type ProfileRef = {
  id: string
  serviceName: string
  // The board id to send for the profile's board selection (e.g. a Pinterest
  // board) — the board's id on the network, not Buffer's own id for it. `null`
  // when the service has no boards or none is selected.
  selectedBoardServiceId: string | null
  // Whether the channel supports Instagram location tagging — Graph-API
  // business/creator channels only; IG-Login and personal-profile channels
  // must never receive geolocation. Derived with the same predicate the
  // location bar uses to hide its picker (see LocationComposerBar).
  supportsLocationTagging: boolean
}

// Ambient values injected into the mappers (the legacy pipeline read these from
// singletons/globals) so the mappers stay pure and unit-testable.
export type MapperContext = {
  channelId: string
  // Which mutation the mapped input feeds; lets a mapper clear a field on edit
  // (explicit `null`) while omitting it on create.
  operation: 'create' | 'edit'
  // Per-profile like `channelId`: the board id to send for the target channel's
  // board selection (e.g. a Pinterest board), as identified on the network.
  // `null` when there is none.
  selectedBoardServiceId: string | null
  // Per-profile like `channelId`: whether the target channel supports
  // Instagram location tagging (Graph-API business/creator only; IG-Login and
  // personal-profile channels must never receive geolocation).
  supportsLocationTagging: boolean
  selectedTagIds: string[]
  source?: string
  // Unix seconds; converted to an ISO `DateTime` for `dueAt`.
  customScheduleTime?: number | null
  // Edit only: the post's send time when the composer opened (unix seconds),
  // used to detect whether the user actually changed the schedule. Queue-derived
  // times count as "no user selection" here — see `resolveEditScheduling`.
  initialScheduledAt?: number | null
  scheduleOption: ScheduleOption
  // Not derivable from `scheduleOption` alone, so the save hook passes it.
  saveToDraft?: boolean
  // The approval intent behind this save, tri-state: absent leaves the approval
  // state alone, `true` requests approval, `false` withdraws a pending request.
  // Each mapper renders it in its own transport shape — create has no prior
  // state so it only needs the boolean `needsApproval`, while edit carries the
  // three cases as `approvalChange`.
  requestApproval?: boolean
  ideaId?: string
  draftId?: string
  // Set by `buildCreatePostInputs` when the channel has a mirrored variant.
  // A migrated metadata mapper reads its fields instead of the `Draft` it's
  // still passed. Absent (flag off, or no variant yet): every mapper reads
  // `Draft`, unchanged.
  channelVariant?: ChannelVariant
  // Set by `buildCreatePostInputs`/`buildEditPostInput` when
  // `composer-zustand-editor-state` is on: the same `BufferEditor` instance
  // `editorSlice` mirrors from `draft.editorState` (identical by reference,
  // never a clone — see CORE-7187). A migrated metadata mapper (annotations)
  // reads this instead of `draft.editorState` directly. Absent (flag off, or
  // not yet mirrored for this draft): every mapper reads `draft.editorState`,
  // unchanged.
  editor?: BufferEditor
}
